import React, { useCallback } from 'react';
import DownloadStatusContext from './DownloadStatus.context';
import { DOWNLOAD_BANNER_STATUS, DOWNLOAD_BANNER_TYPE } from 'models/Download';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import {
  DownloadedSuccessfullyEventData,
  DownloadFailedEventData,
} from 'services/socketService/socketEventData';
import downloadService from 'services/downloadService';
import utils from 'utils';
import DownloadStatusBanner from 'components/DownloadStatusBanner';
import useDownloadBanner from 'hooks/useDownloadBanner';
import { NEW_WINDOW_PROPS } from 'data/global';
import { useTranslation } from 'react-i18next';
import useExportDownloadStatus from './useExportDownloadStatus';

const DownloadStatusProvider: React.FC = (props) => {
  const { children } = props;

  const {
    downloadStatus,
    downloadType,
    downloadBannerProps,
    downloadBannerRef,
    showDownloadBanner,
  } = useDownloadBanner();

  const { t } = useTranslation();

  const startFileDownload = useCallback(
    () =>
      showDownloadBanner({
        status: DOWNLOAD_BANNER_STATUS.PREPARING,
        type: DOWNLOAD_BANNER_TYPE.FILE,
      }),
    [showDownloadBanner],
  );

  const failFileDownload = useCallback(
    () =>
      showDownloadBanner({
        status: DOWNLOAD_BANNER_STATUS.ERROR,
        type: DOWNLOAD_BANNER_TYPE.FILE,
      }),
    [showDownloadBanner],
  );

  const downloadSuccessfully = useCallback(
    (
      { url, type, uniqueId }: DownloadedSuccessfullyEventData,
      downloadType = DOWNLOAD_BANNER_TYPE.FILE,
      shoulOpenInNewWindow = false,
    ) => {
      if (uniqueId !== downloadService.downloadUniqueId) return;

      if (url)
        utils.downloadUrl({
          type,
          href: url,
          ...(shoulOpenInNewWindow ? NEW_WINDOW_PROPS : {}),
        });

      showDownloadBanner({
        status: DOWNLOAD_BANNER_STATUS[url ? 'SUCCESS' : 'ERROR'],
        type: downloadType,
      });
    },
    [showDownloadBanner],
  );

  const downloadFailed = useCallback(
    (
      { uniqueId }: DownloadFailedEventData,
      downloadType = DOWNLOAD_BANNER_TYPE.FILE,
    ) => {
      if (uniqueId !== downloadService.downloadUniqueId) return;

      showDownloadBanner({
        status: DOWNLOAD_BANNER_STATUS.ERROR,
        type: downloadType,
      });
    },
    [showDownloadBanner],
  );

  const {
    isInProgress: isAnalyticsExportInProgress,
    start: startAnalyticsExportDownload,
    fail: failAnalyticsExportDownload,
    showInProgressBanner: showAnalyticsExportInProgressBanner,
  } = useExportDownloadStatus(DOWNLOAD_BANNER_TYPE.ANALYTICS, {
    showDownloadBanner,
    downloadSuccessfully,
    downloadFailed,
  });

  const {
    isInProgress: isLeadAnalyticsExportInProgress,
    start: startLeadAnalyticsExportDownload,
    fail: failLeadAnalyticsExportDownload,
  } = useExportDownloadStatus(DOWNLOAD_BANNER_TYPE.LEAD_ANALYTICS, {
    showDownloadBanner,
    downloadSuccessfully,
    downloadFailed,
  });

  const {
    isInProgress: isBossModeExportInProgress,
    start: startBossModeExportDownload,
    fail: failBossModeExportDownload,
  } = useExportDownloadStatus(DOWNLOAD_BANNER_TYPE.BOSS_MODE, {
    showDownloadBanner,
    downloadSuccessfully,
    downloadFailed,
  });

  const {
    isInProgress: isRfiCategoriesExportInProgress,
    start: startRfiCategoriesExportDownload,
    fail: failRfiCategoriesExportDownload,
  } = useExportDownloadStatus(DOWNLOAD_BANNER_TYPE.RFI_CATEGORIES, {
    showDownloadBanner,
    downloadSuccessfully,
    downloadFailed,
  });

  const {
    isInProgress: isEnterpriseUsageReportExportInProgress,
    start: startEnterpriseUsageReportExport,
    fail: failEnterpriseUsageReportExportDownload,
  } = useExportDownloadStatus(DOWNLOAD_BANNER_TYPE.ENTERPRISE_USAGE_REPORT, {
    showDownloadBanner,
    downloadSuccessfully,
    downloadFailed,
  });

  const startEnterpriseUsageReportExportDownload = useCallback(
    () => startEnterpriseUsageReportExport(undefined, true),
    [startEnterpriseUsageReportExport],
  );

  const { start: startReportExport, fail: failReportDownload } =
    useExportDownloadStatus(DOWNLOAD_BANNER_TYPE.REPORT, {
      showDownloadBanner,
      downloadSuccessfully,
      downloadFailed,
    });

  const startReportDownload = useCallback(
    (openInNewTab = false) =>
      startReportExport(t('reportExportInProgressContent'), openInNewTab),
    [startReportExport, t],
  );

  useSocketEvent(LISTEN_EVENTS.DOWNLOAD_SUCCESSFULLY, downloadSuccessfully);
  useSocketEvent(LISTEN_EVENTS.DOWNLOAD_FAILED, downloadFailed);

  return (
    <DownloadStatusContext.Provider
      value={{
        isAnalyticsExportInProgress,
        isLeadAnalyticsExportInProgress,
        isBossModeExportInProgress,
        isRfiCategoriesExportInProgress,
        isEnterpriseUsageReportExportInProgress,
        startFileDownload,
        failFileDownload,
        startReportDownload,
        failReportDownload,
        showAnalyticsExportInProgressBanner,
        startAnalyticsExportDownload,
        failAnalyticsExportDownload,
        startLeadAnalyticsExportDownload,
        failLeadAnalyticsExportDownload,
        startBossModeExportDownload,
        failBossModeExportDownload,
        startRfiCategoriesExportDownload,
        failRfiCategoriesExportDownload,
        startEnterpriseUsageReportExportDownload,
        failEnterpriseUsageReportExportDownload,
        showDownloadBanner,
      }}
    >
      {children}

      <DownloadStatusBanner
        ref={downloadBannerRef}
        variant={downloadStatus}
        type={downloadType}
        {...downloadBannerProps}
        onClose={() => showDownloadBanner(null)}
      />
    </DownloadStatusContext.Provider>
  );
};

export default DownloadStatusProvider;
